import React from 'react';
import { connect } from 'react-redux';
import type FinancingTermsOverview from '../../../../contracts/FinancingTermsOverview';
import type { RootState } from '../../../../store/types';
import Tooltip from '../../../../shared/components/Tooltip';
import { financingTermsSelectors } from '../../ducks';
import {
  TermsOverviewWrapper,
  TermTitle,
  TermValue,
  Terms,
  LineBreak,
  Question,
  TooltipContent,
  TooltipTitle,
  TooltipBody,
  Months,
  BrowseButton,
  BrowseContainer,
  BrowseTextContainer,
  // BrowseImg,
  TermsContainer,
  OverviewWrapper,
  GetStartedTopText,
  GetStartedBottomText,
  SearchDivider
} from './TermsOverview.styles';

const EXAMPLE_TERMS_CIVIC_URL = `${process.env.CARVANA_APP_ASSETS_BASE_URL}/account-ui-assets/example-terms-civic.png`;

const percentageFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const currencyFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

type StateProps = {
  financingTermsOverview: FinancingTermsOverview | null;
};

function TermsOverview({ financingTermsOverview }: StateProps) {
  if (!financingTermsOverview) return null;

  const searchUrlParams = btoa(
    `{"filters":{"finance":{"downPayment":${financingTermsOverview.minDownPayment}}},"sortBy":"MostPopular"}`
  );

  return (
    <TermsOverviewWrapper>
      <OverviewWrapper>
        <TermsContainer>
          <Terms>
            <TermTitle>
              Starting Interest Rate
              <Tooltip>
                <Tooltip.HoverElement>
                  <Question />
                </Tooltip.HoverElement>
                <TooltipContent direction="down">
                  <TooltipTitle>Is this a fair rate?</TooltipTitle>
                  <TooltipBody>
                    Your APR depends on factors such as your credit history, the vehicle being
                    purchased, the length of the loan and your down payment.
                  </TooltipBody>
                </TooltipContent>
              </Tooltip>
            </TermTitle>
            <TermValue>
              {percentageFormatter.format(financingTermsOverview.minAprRate * 100)}%
            </TermValue>
          </Terms>
          <LineBreak />
          <Terms>
            <TermTitle>
              Minimum Cash Down
              <Tooltip>
                <Tooltip.HoverElement>
                  <Question />
                </Tooltip.HoverElement>
                <TooltipContent direction="down">
                  <TooltipTitle>Minimum Cash Down</TooltipTitle>
                  <TooltipBody>
                    Minimum down payments can vary by vehicle and are non-negotiable. This is the
                    lowest down payment you have been approved for on any of our cars.
                  </TooltipBody>
                </TooltipContent>
              </Tooltip>
            </TermTitle>
            <TermValue>
              ${currencyFormatter.format(financingTermsOverview.minDownPayment)}
            </TermValue>
          </Terms>
          <LineBreak />
          <Terms>
            <TermTitle>
              Max Term
              <Tooltip>
                <Tooltip.HoverElement>
                  <Question />
                </Tooltip.HoverElement>
                <TooltipContent direction="down">
                  <TooltipTitle>Max length available for your loan.</TooltipTitle>
                  <TooltipBody>
                    Your loan period can be shortened but keep in mind that this may increase your
                    monthly payments.
                  </TooltipBody>
                </TooltipContent>
              </Tooltip>
            </TermTitle>
            <TermValue>
              {financingTermsOverview.maxLoanTerm}
              <Months> mo.</Months>
            </TermValue>
          </Terms>
          <LineBreak />
          <Terms>
            <TermTitle>
              Max Loan Payment
              <Tooltip>
                <Tooltip.HoverElement>
                  <Question />
                </Tooltip.HoverElement>
                <TooltipContent direction="down">
                  <TooltipTitle>Max Monthly Payment available for your loan.</TooltipTitle>
                  <TooltipBody>
                    Max payment depends on your income, credit history, and the vehicle and products
                    being purchased. Monthly payments above this amount might lead to increased down
                    payments.
                  </TooltipBody>
                </TooltipContent>
              </Tooltip>
            </TermTitle>
            <TermValue>
              ${Math.ceil(financingTermsOverview.maxPayment)}
              <Months> mo.</Months>
            </TermValue>
          </Terms>
        </TermsContainer>
      </OverviewWrapper>
      <BrowseContainer>
        <img src={EXAMPLE_TERMS_CIVIC_URL} alt="example terms with honda civic" />

        <BrowseTextContainer>
          <SearchDivider />
          <GetStartedTopText>Get started and search</GetStartedTopText>
          <GetStartedBottomText>Cars within your terms</GetStartedBottomText>
          <BrowseButton href={`/cars/filters?cvnaid=${searchUrlParams}`}>
            Browse All Vehicles
          </BrowseButton>
        </BrowseTextContainer>
      </BrowseContainer>
    </TermsOverviewWrapper>
  );
}

const mapStateToProps = (state: RootState) => ({
  financingTermsOverview: financingTermsSelectors.getFinancingTermsOverview(state)
});

export default connect(mapStateToProps)(TermsOverview);
